/**
 * Свод за период (§8, SCREENS §10).
 *
 * Аналитика отвечает на вопрос, которого нет у других приложений: не «куда
 * ушли деньги», а «уложился ли в план». Поэтому расходы делятся на «повседневные нужды»
 * и «свободные траты», и только на них.
 *
 * Накопления в шкалу расходов не попадают ни при каких условиях (И4:
 * отложить — не потратить). Полоса «Могу + Хочу + Коплю» в одной шкале
 * расходов запрещена — она внушает, что накопление это трата.
 */
import { ZERO, add, sub, type Money } from './money.js';
import type { PlainDate } from './date.js';
import type { PlanPart } from './types.js';

/**
 * Операция глазами аналитики.
 *
 * Описана строением, а не импортом типа из приложения: ядро не знает про
 * хранилище, а совпадать полями этого достаточно.
 */
/**
 * Связь операции с тем, что она исполняет.
 *
 * Описана строением, как и сама операция: ядро не знает про хранилище, а
 * совпадать полями этого достаточно. Без связи платёж по обязательству
 * попадал бы и в «обязательства», и в «жизнь» одновременно.
 */
export type TxLinkRef =
  | {
    kind: 'income';
    incomeStreamId?: string;
    receiptId?: string;
    /** Вид поступления: возврат доходом не является. */
    incomeKind?: 'regular' | 'oneoff' | 'refund';
  }
  | { kind: 'obligation'; obligationId: string; instanceId: string }
  | { kind: 'goal'; goalId: string }
  | { kind: 'loan'; loanId: string };

export interface AnalyticsTx {
  type: 'expense' | 'income' | 'transfer' | 'adjustment';
  occurredDate: PlainDate;
  amount: Money;
  /** Откуда — у перевода. Нужен, чтобы узнать внесение на кредитку. */
  accountId?: string;
  /** Куда — у перевода. */
  counterAccountId?: string;
  categoryId?: string;
  part?: PlanPart;
  link?: TxLinkRef;
}

export interface CategorySlice {
  categoryId: string;
  sum: Money;
  /** Целые проценты, сумма долей ровно 100. */
  percent: number;
}

export interface MonthReport {
  /** 'YYYY-MM' */
  month: string;
  expense: Money;
  income: Money;
  /** По убыванию суммы, при равенстве — по идентификатору для устойчивости. */
  byCategory: CategorySlice[];
  need: Money;
  want: Money;
  /** Расходных операций в периоде: по нему отличается «пусто» от «мало». */
  count: number;
}

/** 'YYYY-MM' даты. */
export const monthOf = (d: PlainDate): string => d.slice(0, 7);

/** Месяц, сдвинутый на n месяцев. */
export function shiftMonth(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  const total = y * 12 + (m - 1) + n;
  const yy = Math.floor(total / 12);
  const mm = (total % 12) + 1;
  return `${String(yy).padStart(4, '0')}-${String(mm).padStart(2, '0')}`;
}

/** Месяц даты «сегодня» — отдельная функция, чтобы время передавалось параметром. */
export const currentMonth = (today: PlainDate): string => monthOf(today);

export function monthReport(txs: readonly AnalyticsTx[], month: string): MonthReport {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const r = rangeReport(txs, `${month}-01` as PlainDate, `${month}-${String(last).padStart(2, '0')}` as PlainDate);
  return {
    month, expense: r.expense, income: r.income, byCategory: r.byCategory, need: r.need, want: r.want, count: r.count,
  };
}

/** Свод за любой отрезок: расходы по категориям и поступления по категориям. */
export interface RangeReport {
  expense: Money;
  income: Money;
  byCategory: CategorySlice[];
  /** Поступления по категориям, без возвратов. */
  incomeByCategory: CategorySlice[];
  need: Money;
  want: Money;
  count: number;
  /** Поступлений в отрезке (без возвратов). */
  incomeCount: number;
}

/**
 * Свод за отрезок дат — тем же правилом, что и месяц.
 *
 * `null` — без границы («Всё время»). Аналитика больше не привязана к
 * календарному месяцу: период от зарплаты до зарплаты, неделя, год.
 */
export function rangeReport(
  txs: readonly AnalyticsTx[], from: PlainDate | null, to: PlainDate | null,
): RangeReport {
  let expense = ZERO;
  let income = ZERO;
  let need = ZERO;
  let want = ZERO;
  let count = 0;
  let incomeCount = 0;
  const byCat = new Map<string, Money>();
  const byIncomeCat = new Map<string, Money>();

  for (const t of txs) {
    if (from && t.occurredDate < from) continue;
    if (to && t.occurredDate > to) continue;
    // Перевод между своими счетами расходом не является (И2): капитал не
    // изменился, деньги сменили счёт. Попади он в аналитику — каждое
    // погашение кредитки выглядело бы новой тратой.
    if (t.type === 'transfer') continue;
    // Корректировка сверки не расход и не доход: она приводит баланс к
    // реальности, а не описывает событие. В категориях ей места нет.
    if (t.type === 'adjustment') continue;

    if (t.type === 'income') {
      /*
        Возврат — не заработок, а отмена прошлой траты.

        Посчитать его доходом значило бы дважды солгать: месяц выглядел бы
        прибыльнее, чем был, а категория, куда деньги вернулись, — дороже.
        Поэтому возврат уменьшает расход там же, где он возник.
      */
      if (t.link?.kind === 'income' && t.link.incomeKind === 'refund') {
        expense = sub(expense, t.amount);
        if (t.part === 'want') want = sub(want, t.amount);
        else need = sub(need, t.amount);
        const back = t.categoryId ?? 'other';
        byCat.set(back, sub(byCat.get(back) ?? ZERO, t.amount));
        continue;
      }
      income = add(income, t.amount);
      incomeCount += 1;
      const id = t.categoryId ?? 'income-other';
      byIncomeCat.set(id, add(byIncomeCat.get(id) ?? ZERO, t.amount));
      continue;
    }

    expense = add(expense, t.amount);
    count += 1;
    if (t.part === 'want') want = add(want, t.amount);
    else need = add(need, t.amount);
    const id = t.categoryId ?? 'other';
    byCat.set(id, add(byCat.get(id) ?? ZERO, t.amount));
  }

  const sorted = (m: Map<string, Money>) => [...m.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return {
    expense,
    income,
    byCategory: withPercents(sorted(byCat), expense),
    incomeByCategory: withPercents(sorted(byIncomeCat), income),
    need,
    want,
    count,
    incomeCount,
  };
}

/**
 * Проценты, сумма которых ровно 100.
 *
 * Округление каждой доли по отдельности даёт в сумме то 99, то 101 — и человек
 * справедливо перестаёт верить числам. Здесь тот же способ, что и для денег:
 * раздать сто единиц по наибольшим остаткам.
 */
function withPercents(rows: [string, Money][], total: Money): CategorySlice[] {
  if (rows.length === 0 || total <= 0) {
    return rows.map(([categoryId, sum]) => ({ categoryId, sum, percent: 0 }));
  }
  // Способ тот же, что для денег в distribute, но проценты деньгами не
  // являются, и выдавать их за Money ради переиспользования не стоит.
  const exact = rows.map(([, sum]) => (sum * 100) / total);
  const floors = exact.map(Math.floor);
  let left = 100 - floors.reduce((a, b) => a + b, 0);
  const order = exact
    .map((e, i) => ({ i, frac: e - Math.floor(e) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  const percent = [...floors];
  for (const { i } of order) {
    if (left <= 0) break;
    percent[i] = (percent[i] ?? 0) + 1;
    left -= 1;
  }
  return rows.map(([categoryId, sum], i) => ({ categoryId, sum, percent: percent[i] ?? 0 }));
}

/**
 * Хвост категорий в «Остальное».
 *
 * Пончик из двадцати секторов не читается, а палитра проверена на шести
 * (§8.6). Седьмая и далее сворачиваются в одну долю, которую можно раскрыть.
 */
export function collapseTail(slices: readonly CategorySlice[], max: number): {
  head: CategorySlice[];
  tail: CategorySlice[];
  rest: CategorySlice | null;
} {
  if (slices.length <= max) return { head: [...slices], tail: [], rest: null };
  const head = slices.slice(0, max - 1);
  const tail = slices.slice(max - 1);
  const sum = tail.reduce((a, s) => add(a, s.sum), ZERO);
  const percent = tail.reduce((a, s) => a + s.percent, 0);
  return { head, tail: [...tail], rest: { categoryId: 'rest', sum, percent } };
}

/**
 * Тот же месяц, но только первые N дней.
 *
 * Неполный месяц сравнивать с полным нельзя: двадцать один день сентября
 * против тридцати одного августовского — это не «стало лучше», а разная
 * длина линейки. Отбор живёт здесь, рядом с `monthReport`, чтобы экраны не
 * заводили свой способ резать период.
 */
export const throughDay = <T extends { occurredDate: PlainDate }>(
  txs: readonly T[], month: string, day: number,
): T[] => txs.filter(
  (t) => monthOf(t.occurredDate) === month && Number(t.occurredDate.slice(8, 10)) <= day,
);

/** Название месяца по-русски: «сентябрь 2026». */
export function monthTitle(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const names = [
    'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
    'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
  ];
  if (!y || !m || m < 1 || m > 12) return month;
  return `${names[m - 1]} ${y}`;
}

/**
 * Месяц как отчёт: что случилось, а не что можно потратить.
 *
 * Разделение принципиальное. На вопрос «сколько я могу сегодня» отвечает
 * оперативная раскладка — она построена на деньгах, которые есть. Месяц
 * отвечает на другой вопрос: сколько пришло, сколько ушло и выполнены ли
 * договорённости с целями. Смешивать их нельзя: месячный экран, говорящий
 * «можно потратить», снова начнёт обещать ожидаемый доход.
 *
 * Поэтому здесь только факт:
 *
 * · доход — подтверждённые поступления, без ожидаемых;
 * · переводы между своими счетами не считаются ни доходом, ни расходом;
 * · возврат уменьшает свой расход, а не увеличивает доход;
 * · взнос в цель — не расход: отложить не значит потратить;
 * · платёж по обязательству считается один раз, по связи с наступлением.
 */
export interface MonthFacts {
  month: string;
  /** Фактически пришло: подтверждённые доходы, кроме возвратов. */
  income: Money;
  /** Оплачено обязательств — по связи с наступлениями. */
  obligations: Money;
  /** Обычная жизнь: расходы `need` без платежей по обязательствам. */
  need: Money;
  /** Свободные траты: расходы `want`. */
  want: Money;
  /** Внесено в цели — не расход. */
  goals: Money;
  /** Снято из целей — тоже не расход и не доход: деньги вернулись в оборот. */
  goalsWithdrawn: Money;
  /** Возвраты: уже вычтены из своих трат, доходом не считаются. */
  refunds: Money;
  /**
   * Внесено на кредитку: переводы со своих счетов на кредитную карту.
   *
   * Отдельной строкой и **вне расходов**. Покупка картой — расход в день
   * покупки; погашение — исполнение платежа, и посчитать его ещё раз значило
   * бы удвоить траты. «Внесено», а не «погашено»: внесённое сверх долга
   * стало своими деньгами на карте. Проценты и комиссии записываются
   * расходами и остаются ими.
   */
  toCards: Money;
  /**
   * Получено из своего буфера стабильного бюджета.
   *
   * Отдельной строкой и **вне дохода**. Это движение собственных денег:
   * человек переложил их из буфера в бюджет. Сложить их с внешним доходом
   * значило бы удвоить заработок — один раз когда деньги пришли от
   * заказчика, второй когда человек выплатил их себе.
   */
  fromBuffer: Money;
  /**
   * Сверка запаса за период: разовая поправка остатка, а не выплата.
   *
   * Появляется после долгого отсутствия, когда человек подтвердил, что
   * деньги брал, но сумм по датам не знает никто. Отдельной строкой, чтобы
   * агрегатная поправка не выглядела обычной выплатой.
   */
  bufferReconciled: Money;
  /**
   * Остаток буфера на начало и конец месяца. `null` — режима нет.
   *
   * Изменение остатка и есть ответ на вопрос «я проедаю запас или коплю»:
   * без него месяц с большим приходом и месяц, прожитый из буфера, выглядят
   * одинаково.
   */
  buffer: { start: Money; end: Money } | null;
  /**
   * Месяц неполный: приложение начало вести учёт позже первого числа.
   *
   * `null` — месяц прожит целиком под наблюдением. Иначе дата, с которой
   * факт достоверен: остальное человек тратил мимо приложения, и
   * восстанавливать это догадкой нельзя.
   */
  partialSince: PlainDate | null;
}

export interface MonthFactsInput {
  txs: readonly AnalyticsTx[];
  month: string;
  /** Взносы в цели за месяц: они живут в журнале целей, а не в операциях. */
  goalContributions?: Money;
  /** С какого дня приложение ведёт учёт. */
  trackingStartedAt?: PlainDate;
  /**
   * Выплаты себе за этот месяц — по дню **фактического** движения денег.
   *
   * Не по плановой дате: просроченная августовская выплата, проведённая
   * 21 сентября, — сентябрьский факт. Отбор делает вызывающий, здесь
   * остаётся арифметика.
   */
  selfPayouts?: readonly { date: PlainDate; amount: Money }[];
  /** Поправка остатка запаса по сверке за этот месяц. Со знаком. */
  bufferReconciled?: Money;
  /** Остаток буфера на начало и конец месяца, если режим включён. */
  buffer?: { start: Money; end: Money };
  /** Снято из целей за месяц. */
  goalWithdrawals?: Money;
  /** Счета-кредитки: перевод на них — «Внесено на кредитку». */
  creditCardIds?: readonly string[];
}

/** Вход единого подсчёта фактов: любой отрезок дат, а не только месяц. */
export interface PeriodFactsInput {
  txs: readonly AnalyticsTx[];
  /** Отрезок фактов, оба конца включительно. */
  from: PlainDate;
  to: PlainDate;
  /**
   * Журнал целей: взносы и снятия по дням.
   *
   * Цели живут в журнале, а не в операциях: взнос без перевода между
   * счетами операции не создаёт, а с переводом — создаёт её и запись в
   * журнале, и считать по обоим значило бы удвоить.
   */
  goalEvents?: readonly { date: PlainDate; kind: string; amount: Money }[];
  /** Выплаты себе — по дню **фактического** движения денег. */
  selfPayouts?: readonly { date: PlainDate; amount: Money }[];
  /** Поправка остатка запаса по сверке за отрезок. Со знаком. */
  bufferReconciled?: Money;
  /** Счета-кредитки: перевод на них — «Внесено на кредитку». */
  creditCardIds?: readonly string[];
}

export interface PeriodFacts {
  income: Money;
  refunds: Money;
  obligations: Money;
  need: Money;
  want: Money;
  goals: Money;
  goalsWithdrawn: Money;
  fromBuffer: Money;
  bufferReconciled: Money;
  toCards: Money;
  /**
   * Корректировки остатков: разница между записанным и тем, что человек
   * увидел на счёте. Со знаком: минус — денег оказалось меньше. Не траты и не
   * доход — отдельной строкой, чтобы итоги сходились со счетами.
   */
  adjustments: Money;
}

/**
 * Что случилось за отрезок — **один подсчёт на всё приложение**.
 *
 * Раньше их было два, и они расходились: план периода считал возврат
 * доходом, а месячный отчёт — уменьшением траты; план искал взносы в цели в
 * операциях, а отчёт — в журнале целей. Два ответа на «сколько я потратил»
 * — это ни одного. Правила:
 *
 * · перевод между своими счетами — ни доход, ни расход; перевод на
 *   кредитку — «Внесено на кредитку», тоже не расход;
 * · возврат уменьшает ту трату, из которой он пришёл, и доходом не является;
 * · платёж по обязательству — своя строка, а не «жизнь»;
 * · взнос в цель — не расход; снятие из цели — не доход; оба по журналу;
 * · выплата себе из запаса — своя строка, не заработок.
 */
export function periodFacts(input: PeriodFactsInput): PeriodFacts {
  const inside = (d: PlainDate) => d >= input.from && d <= input.to;
  const cards = new Set(input.creditCardIds ?? []);
  let income = ZERO;
  let refunds = ZERO;
  let obligations = ZERO;
  let need = ZERO;
  let want = ZERO;
  let toCards = ZERO;
  let adjustments = ZERO;

  for (const t of input.txs) {
    if (!inside(t.occurredDate)) continue;
    if (t.type === 'adjustment') {
      adjustments = add(adjustments, t.amount);
      continue;
    }
    if (t.type === 'transfer') {
      if (t.counterAccountId && cards.has(t.counterAccountId) && t.accountId !== t.counterAccountId) {
        toCards = add(toCards, t.amount);
      }
      continue;
    }

    if (t.type === 'income') {
      if (t.link?.kind === 'income' && t.link.incomeKind === 'refund') {
        refunds = add(refunds, t.amount);
        if (t.part === 'want') want = sub(want, t.amount);
        else need = sub(need, t.amount);
        continue;
      }
      income = add(income, t.amount);
      continue;
    }

    if (t.link?.kind === 'obligation') {
      obligations = add(obligations, t.amount);
      continue;
    }
    // Взнос в цель операцией-расходом — не трата; сам взнос считает журнал.
    if (t.link?.kind === 'goal') continue;

    if (t.part === 'want') want = add(want, t.amount);
    else need = add(need, t.amount);
  }

  const events = (input.goalEvents ?? []).filter((e) => inside(e.date));
  return {
    income,
    refunds,
    obligations,
    need,
    want,
    goals: add(...events.filter((e) => e.kind === 'contribution').map((e) => e.amount)),
    goalsWithdrawn: add(...events.filter((e) => e.kind === 'withdrawal').map((e) => e.amount)),
    fromBuffer: add(...(input.selfPayouts ?? []).filter((p) => inside(p.date)).map((p) => p.amount)),
    bufferReconciled: input.bufferReconciled ?? ZERO,
    toCards,
    adjustments,
  };
}

export function monthFacts(input: MonthFactsInput): MonthFacts {
  const [y, m] = input.month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const facts = periodFacts({
    txs: input.txs,
    from: `${input.month}-01` as PlainDate,
    to: `${input.month}-${String(last).padStart(2, '0')}` as PlainDate,
    ...(input.selfPayouts ? { selfPayouts: input.selfPayouts } : {}),
    ...(input.bufferReconciled !== undefined ? { bufferReconciled: input.bufferReconciled } : {}),
    ...(input.creditCardIds ? { creditCardIds: input.creditCardIds } : {}),
  });

  const since = input.trackingStartedAt;
  const partialSince = since && monthOf(since) === input.month && !since.endsWith('-01')
    ? since
    : null;

  return {
    month: input.month,
    ...facts,
    // Взносы месяца приходят суммой: так их уже передаёт отчёт.
    goals: input.goalContributions ?? ZERO,
    goalsWithdrawn: input.goalWithdrawals ?? ZERO,
    buffer: input.buffer ?? null,
    partialSince,
  };
}
